import { isAddedDateSlot, toDateOnly, type ClassSessionWindow, type SlotWithDateWindow } from './oneOffScheduleSlots';

export interface WeeklySlot extends SlotWithDateWindow {
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface WeeklyDaySummary {
  /** Weekdays the schedule meets (0 = Sunday … 6 = Saturday), sorted, no repeats. */
  days: number[];
  /** The one start/end every counted row shares, or null when they differ. */
  time: { start_time: string; end_time: string } | null;
}

/**
 * The weekly days a schedule meets, as the public class pages show them.
 * The catalog card and the class detail page both call this so they never
 * disagree about a class's days.
 *
 * - One-off added dates are never counted: they meet once, not weekly.
 * - Rows in effect today win. A changeover row starting later is ignored
 *   while the current row runs.
 * - When nothing is in effect yet (a class listed before its first class),
 *   the rows with the earliest upcoming start date are used instead.
 */
export function summarizeWeeklyDays<S extends WeeklySlot>(
  rows: S[] | null | undefined,
  classWindow: ClassSessionWindow | null | undefined,
  todayStr: string,
): WeeklyDaySummary {
  const counted = currentWeeklyRows(rows, classWindow, todayStr);

  const days = Array.from(new Set(counted.map((row) => row.day_of_week))).sort((a, b) => a - b);
  const times = new Set(counted.map((row) => `${row.start_time}|${row.end_time}`));
  const time = times.size === 1
    ? { start_time: counted[0].start_time, end_time: counted[0].end_time }
    : null;

  return { days, time };
}

/** The weekly rows `summarizeWeeklyDays` counts, by the rules listed on it. */
export function currentWeeklyRows<S extends SlotWithDateWindow>(
  rows: S[] | null | undefined,
  classWindow: ClassSessionWindow | null | undefined,
  todayStr: string,
): S[] {
  const weekly = (rows || []).filter((row) => !isAddedDateSlot(row, classWindow));
  const notEnded = weekly.filter((row) => {
    const end = toDateOnly(row.end_date);
    return !end || end >= todayStr;
  });

  let counted = notEnded.filter((row) => {
    const start = toDateOnly(row.effective_date);
    return !start || start <= todayStr;
  });
  if (counted.length === 0) {
    const starts = notEnded
      .map((row) => toDateOnly(row.effective_date))
      .filter((start): start is string => start !== null)
      .sort();
    const firstStart = starts[0];
    counted = firstStart ? notEnded.filter((row) => toDateOnly(row.effective_date) === firstStart) : [];
  }
  return counted;
}

export interface WeeklyDayGroup extends ClassSessionWindow {
  id: string;
  section_name: string | null;
  /** 1-based place in the class's full schedule list, for an unnamed group's "Schedule N". */
  schedule_number: number;
  schedules: WeeklySlot[];
}

/**
 * The rows the catalog card hands to `summarizeWeeklyDays`, one entry per
 * schedule group. Built from EVERY group and every row not switched off
 * (`is_active = false`), and not from the catalog's booking list: that list
 * hides a row that starts later, so mid-changeover (old row ended, new row
 * starts next week) it would leave the card with no days. Pass the groups in
 * the order the class page lists them, so "Schedule N" matches there too.
 */
export function toWeeklyDayGroups(sections: any[] | null | undefined): WeeklyDayGroup[] {
  return (sections || []).map((section, index) => ({
    id: section.id,
    section_name: section.section_name ?? null,
    schedule_number: index + 1,
    session_start_date: section.session_start_date ?? null,
    session_end_date: section.session_end_date ?? null,
    schedules: (section.schedules || [])
      .filter((row: any) => row.is_active !== false)
      .map((row: any) => ({
        day_of_week: row.day_of_week,
        start_time: row.start_time,
        end_time: row.end_time,
        effective_date: row.effective_date ?? null,
        end_date: row.end_date ?? null,
      })),
  }));
}
